/**
 * The planet is the prize (IDEAS X26): what holding a system is worth, and the one rule under it — what you wreck,
 * you own less of. The income side exists (56.11: a held system pays by how intact its planets are, with ecotourism
 * on top). This names the worth so the stand-in commander can value systems by their state and the panel can say
 * it. Pure; deterministic.
 */
import type { SectorRules, SectorSystem } from './types';

export interface SystemWorth {
  /** How much of the system's planets is still there, 0 … 1 (1 − the mass lost to craters). */
  intact: number;
  /** Supply a day it pays its holder, at the rules' income, with its structures. */
  incomePerDay: number;
  /** The same for an unscarred system of its kind — what wrecking it has cost. */
  pristinePerDay: number;
  /** The worth the commander ranks by: planets × intact, with a bonus for what is built, and a home counts double. */
  score: number;
}

/** A system's worth to whoever holds it. */
export function systemWorth(sys: Pick<SectorSystem, 'planetCount' | 'scars' | 'structures'>, rules: Pick<SectorRules, 'incomePerSystemPerDay'>, isHome = false): SystemWorth {
  const intact = Math.max(0, 1 - sys.scars.massLostFraction);
  const eco = 1 + 0.5 * sys.structures.filter(b => b.kind === 'ecotourism').length;
  const pristinePerDay = rules.incomePerSystemPerDay * eco;
  const incomePerDay = pristinePerDay * intact;
  const built = sys.structures.length * 0.25;
  const score = (sys.planetCount * intact + built) * (isHome ? 2 : 1);
  return { intact, incomePerDay, pristinePerDay, score };
}

/** The worth in words, for the system panel. */
export function worthSummary(w: SystemWorth): string {
  const intact = `${Math.round(w.intact * 100)}% intact`;
  const pay = w.pristinePerDay > 0 ? `; pays ${w.incomePerDay.toFixed(1)} a day${w.incomePerDay < w.pristinePerDay - 1e-9 ? ` (${w.pristinePerDay.toFixed(1)} unscarred)` : ''}` : '';
  return `${intact}${pay}; worth ${w.score.toFixed(1)}`;
}
